import { useSyncExternalStore } from "react";

/** Ширина боковой панели помощника по умолчанию и её пределы (п. 9 спеки). */
export const DOCK_DEFAULT_WIDTH = 420;
export const DOCK_MIN_WIDTH = 340;
/** Доля ширины окна, больше которой панель не растягивается. */
export const DOCK_MAX_SHARE = 0.45;

const OPEN_KEY = "partnerAssistant.dock.open";
const WIDTH_KEY = "partnerAssistant.dock.width";

/**
 * Ширина панели в пределах [340, 45vw]. На узком окне, где 45vw меньше
 * минимума, побеждает минимум: панель не сжимается до нечитаемой.
 */
export function clampDockWidth(width: number, viewportWidth: number): number {
  const max = Math.max(
    DOCK_MIN_WIDTH,
    Math.floor(viewportWidth * DOCK_MAX_SHARE)
  );
  const value = Number.isFinite(width) ? width : DOCK_DEFAULT_WIDTH;
  return Math.round(Math.min(max, Math.max(DOCK_MIN_WIDTH, value)));
}

/** Сохранённая ширина: мусор и пустое значение дают ширину по умолчанию. */
export function parseStoredWidth(raw: string | null): number {
  const value = raw === null ? NaN : Number(raw);
  return Number.isFinite(value) && value > 0 ? value : DOCK_DEFAULT_WIDTH;
}

const readStorage = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // приватный режим или запрет хранилища: панель работает без памяти
  }
};

export interface DockState {
  /** Открыта ли панель на десктопе (запоминается в браузере). */
  open: boolean;
  /** Желаемая ширина; фактическую даёт clampDockWidth по ширине окна. */
  width: number;
  /** Идёт перетаскивание края: отступ страницы меняется без анимации. */
  resizing: boolean;
}

let state: DockState | null = null;
const listeners = new Set<() => void>();

const getState = (): DockState => {
  if (!state) {
    state = {
      open: readStorage(OPEN_KEY) === "1",
      width: parseStoredWidth(readStorage(WIDTH_KEY)),
      resizing: false,
    };
  }
  return state;
};

const setState = (patch: Partial<DockState>) => {
  state = { ...getState(), ...patch };
  listeners.forEach((l) => l());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const setDockOpen = (open: boolean) => {
  if (getState().open === open) return;
  writeStorage(OPEN_KEY, open ? "1" : "0");
  setState({ open });
};

/** persist: false во время перетаскивания, true по его окончании. */
export const setDockWidth = (width: number, persist: boolean) => {
  if (persist) writeStorage(WIDTH_KEY, String(Math.round(width)));
  setState({ width });
};

export const setDockResizing = (resizing: boolean) => setState({ resizing });

export const useDockState = (): DockState =>
  useSyncExternalStore(subscribe, getState, getState);

const subscribeResize = (listener: () => void) => {
  window.addEventListener("resize", listener);
  return () => window.removeEventListener("resize", listener);
};

const getViewportWidth = () => window.innerWidth;

/** Фактическая ширина панели с учётом текущей ширины окна. */
export const useDockWidth = (): number => {
  const { width } = useDockState();
  const viewport = useSyncExternalStore(
    subscribeResize,
    getViewportWidth,
    getViewportWidth
  );
  return clampDockWidth(width, viewport);
};
